/**
 * Specialized formatting utility for Quick Article Creator
 * Handles HTML content and prevents duplication of sections
 */

/**
 * Format article content to prevent duplication issues and ensure proper HTML structure
 * @param {string} content - Raw article content from AI generation
 * @returns {string} - Properly formatted content
 */
export const formatArticleContent = (content) => {
  // If content is empty or not a string, return as is
  if (!content || typeof content !== 'string') {
    return content;
  }

  let processedContent = content;
  
  // Check if the content has HTML structure and extract body content if needed
  if (processedContent.includes('<html') || processedContent.includes('<body')) {
    try {
      const tempDoc = document.createElement('div');
      tempDoc.innerHTML = processedContent;
      
      // If there's a body tag, extract its content
      const bodyContent = tempDoc.querySelector('body');
      if (bodyContent) {
        processedContent = bodyContent.innerHTML;
      } else {
        // Otherwise just use the div's content
        processedContent = tempDoc.innerHTML;
      }
    } catch (error) {
      console.error('Error extracting body content:', error);
    }
  }

  // Remove any duplicate introduction sections
  // Common pattern: AI sometimes generates an "Introduction" section and then repeats similar content
  try {
    const tempDoc = document.createElement('div');
    tempDoc.innerHTML = processedContent;
    
    // Check for introduction duplication patterns
    const paragraphs = tempDoc.querySelectorAll('p');
    
    if (paragraphs.length > 3) {
      // Check first few paragraphs for similarities (potential duplicates)
      const firstParagraphText = paragraphs[0]?.textContent?.trim().toLowerCase() || '';
      
      // Look for similar paragraphs that might be duplicated introductions
      for (let i = 1; i < Math.min(4, paragraphs.length); i++) {
        const currentParagraph = paragraphs[i]?.textContent?.trim().toLowerCase() || '';
        
        // If paragraphs have significant similarity, potential duplication
        if (firstParagraphText.length > 30 && 
            currentParagraph.length > 30 &&
            (firstParagraphText.includes(currentParagraph.substring(0, 30)) || 
             currentParagraph.includes(firstParagraphText.substring(0, 30)))) {
          
          // If we found a significant duplicate in first few paragraphs, keep only the first one
          paragraphs[i].remove();
          console.log('Removed potential duplicate introduction paragraph');
          break;
        }
      }
      
      processedContent = tempDoc.innerHTML;
    }
  } catch (error) {
    console.error('Error removing duplicate introductions:', error);
  }
  
  // Ensure proper paragraph wrapping
  processedContent = ensureParagraphTags(processedContent);
  
  // Remove any empty headers (common AI model artifact)
  processedContent = processedContent.replace(/<h[1-6]>\s*<\/h[1-6]>/gi, '');
  
  // Remove any "Introduction" heading (common AI artifact - we want intro without a heading)
  processedContent = processedContent.replace(/<h[1-6]>\s*introduction\s*<\/h[1-6]>/gi, '');
  
  // Ensure proper heading hierarchy
  processedContent = processedContent.replace(/<h1/gi, '<h2');
  
  return processedContent;
};

/**
 * Ensure text content is wrapped in paragraph tags
 * @param {string} content - Content to process
 * @returns {string} - Content with proper paragraph tags
 */
const ensureParagraphTags = (content) => {
  // If already properly formatted with paragraphs, return as is
  if (content.match(/<p>[\s\S]*?<\/p>/g)) {
    return content;
  }
  
  // Split by double newlines to identify paragraphs
  const paragraphs = content
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(p => p.length > 0);
  
  // Wrap each identified paragraph in <p> tags
  return paragraphs
    .map(p => {
      // Skip wrapping if it's already a heading, list, or has HTML tags
      if (p.startsWith('<h') || p.startsWith('<ul') || p.startsWith('<ol') || p.startsWith('<div') || p.startsWith('<p')) {
        return p;
      }
      return `<p>${p}</p>`;
    })
    .join('\n\n');
};

export default formatArticleContent; 